<script setup lang="ts">
import { computed } from 'vue';
import GraphCardActionsV3 from '@/components/Common/GraphCardActionsV3.vue';
import {
  AnomalyDataObjectType,
  GraphDataNamesEnum,
} from '@/components/Common/graph.types';
import {
  severityKeyMapper,
  anomalyTypeColorMapper,
} from '@/components/charts/utils/utils.config';
import { useThemeToken } from '@/utils/theme/useThemeToken';
import { formatDate } from '@/utils/time/formatDate';

interface Props {
  selectedAnomalyInfo: AnomalyDataObjectType;
  anomaliesList: AnomalyDataObjectType[];
  handleZoomOut: () => void;
  handleZoomIn: () => void;
  handleReset: () => void;
  actionsIdPrefix: string;
}

const props = defineProps<Props>();
const { token } = useThemeToken();

const getAnomalyCountInfo = computed(() => {
  let anomalyMapper = {
    [severityKeyMapper[1]]: 0,
    [severityKeyMapper[2]]: 0,
    [severityKeyMapper[3]]: 0,
  };

  props.anomaliesList.forEach((obj) => {
    anomalyMapper[severityKeyMapper[obj[GraphDataNamesEnum.SEVERITY]]]++;
  });
  anomalyMapper.total = props.anomaliesList.length;
  return anomalyMapper;
});

const getSelectedAnomalyFormattedData = computed(() => {
  const date =
    props.selectedAnomalyInfo.date &&
    formatDate(
      new Date(props.selectedAnomalyInfo.date).getTime(),
      0,
      'YYYY-MM-DD HH:mm A',
    );

  return {
    color: anomalyTypeColorMapper[props.selectedAnomalyInfo.sevKey || 4],
    label: props.selectedAnomalyInfo.type,
    date: date,
  };
});
</script>

<template>
  <div class="anmly-graph-header-box">
    <div>
      <div
        v-if="selectedAnomalyInfo.sevKey"
        class="anmly-graph-header-title-box"
      >
        <div
          :style="{ backgroundColor: getSelectedAnomalyFormattedData.color }"
          class="anmly-graph-header-sev-circle"
        ></div>
        <span class="anmly-graph-header-word-space">
          {{ getSelectedAnomalyFormattedData.label }}
        </span>
        <span class="anmly-graph-header-word-space">
          .&nbsp;&nbsp;{{ getSelectedAnomalyFormattedData.date }}&nbsp;&nbsp;.
        </span>
      </div>
    </div>

    <div class="anmly-graph-header-info-tools-box">
      <div class="anmly-graph-header-anmly-count-box">
        <span
          class="anmly-graph-header-anmly-count-value"
          :style="{ color: token.colorPrimary }"
        >
          {{ getAnomalyCountInfo.total }}
        </span>
        <span>{{ $t('anomalies') }}</span>
      </div>
      <div class="anmly-graph-header-anmly-count-box">
        <span
          class="anmly-graph-header-anmly-count-value"
          :style="{ color: token.colorPrimary }"
        >
          {{ getAnomalyCountInfo[GraphDataNamesEnum.CRITICAL_BREACH] }}
        </span>
        <span>{{ $t('criticalAnomalies') }}</span>
      </div>
      <div class="anmly-graph-header-anmly-count-box">
        <span
          class="anmly-graph-header-anmly-count-value"
          :style="{ color: token.colorPrimary }"
        >
          {{ getAnomalyCountInfo[GraphDataNamesEnum.MAJOR_BREACH] }}
        </span>
        <span>{{ $t('majorAnomalies') }}</span>
      </div>
      <div class="anmly-graph-header-anmly-count-box">
        <span
          class="anmly-graph-header-anmly-count-value"
          :style="{ color: token.colorPrimary }"
        >
          {{ getAnomalyCountInfo[GraphDataNamesEnum.MINOR_BREACH] }}
        </span>
        <span>{{ $t('minorAnomalies') }}</span>
      </div>

      <GraphCardActionsV3
        :title="`modal-${actionsIdPrefix}`"
        :handleZoomOut="props.handleZoomOut"
        :handleZoomIn="props.handleZoomIn"
        :handleReset="props.handleReset"
        :displayExpand="false"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.anmly-graph-header-box {
  @include flex-center-justify(space-between);
  margin: 1rem;
}
.anmly-graph-header-title-box {
  @include flex-center-vert;
  font-size: 1rem;
}
.anmly-graph-header-sev-circle {
  height: 1rem;
  width: 1rem;
  border-radius: 50%;
  margin-right: 0.25rem;
}
.anmly-graph-header-info-tools-box {
  @include flex-center-vert;
}
.anmly-graph-header-anmly-count-box {
  margin-right: 1rem;
  color: $table-header-text-grey;
  font-size: 0.85rem;
}
.anmly-graph-header-anmly-count-value {
  margin-right: 0.5rem;
}
.anmly-graph-header-word-space {
  margin-right: 0.5rem;
}
</style>
